/** The Week's matrix (DESIGN §16.6): an athlete a row, a day a column, each cell its sessions, each a link to its panel. */
import { type KeyboardEvent, type MouseEvent, type ReactNode, useMemo, useState } from "react";
import { Link } from "react-router";
import { PbMark, Status, byNumber, byText, type Sort } from "../../kit/index.js";
import { useModel } from "../context.js";
import { dayLong, parseDay, weekdayShort } from "../model/days.js";
import { plural } from "../model/plan.js";
import { records, skippedWorkout, weekDays, workoutTitle, type Athlete, type State, type WeekModel } from "../model/week.js";
import { href } from "../paths.js";
import { hasNotes, pinnedFor, type Note } from "../model/notes.js";
import { NoteMark, PinnedLine, useNotes } from "./Notes.js";
import { LoadCell, athleteLoad, loadValue, useLoad, type AthleteLoad } from "./loadParts.js";
import { meetsOn } from "../model/meets.js";
import type { Meet } from "../model/types.js";
import { FlagIcon, useMeets } from "./Meets.js";
import { bodyOf, effortOf, effortWord } from "../model/effort.js";
import { BodyLevel } from "./common.js";

/** What the panel over the matrix shows, so its column or cells stay marked. */
export interface WeekSelection {
  date?: string;
  session?: string;
  track?: string;
}

type CellItem =
  | { kind: "weights"; key: string; to: string | null; session: string | null; title: string; state: State; records: number; skipped: number }
  | { kind: "track"; key: string; to: string; track: string; label: string; logged: boolean; state: State | null; skipped: number };

const STATE_WORD: Record<State, string> = { done: "Done", skipped: "Skipped", missed: "Not logged", today: "Today", planned: "Planned" };

/** `Done · 1 skipped` (§61.8): the sets skipped in a session done, said after its title. */
function skippedText(state: State | null, skipped: number): string | null {
  return state === "done" && skipped > 0 ? skipped + " skipped" : null;
}

export function columnText(a: Athlete): string {
  return a.column ? a.column : a.nothing ? "Nothing this week" : a.noGroup ? "No group" : "";
}

/** One athlete's day: weights sessions given (and any they did off the plan), the rest of the plan in the coach's order. */
export function cellItems(model: WeekModel, a: Athlete, date: string): CellItem[] {
  const items: CellItem[] = [];
  for (const g of a.given.filter((x) => x.date === date)) {
    items.push({ kind: "weights", key: "g" + g.id, to: href(model.monday, "session", g.id), session: g.id, title: g.title, state: g.state, records: records(g.done), skipped: g.skipped });
  }
  a.extras
    .filter((w) => w.date === date)
    .forEach((w, i) => {
      const cs = model.coach.find((c) => c.workouts.includes(w));
      items.push({
        kind: "weights",
        key: "x" + i,
        to: cs ? href(model.monday, "session", cs.id) : null,
        session: cs?.id ?? null,
        title: workoutTitle(model, w),
        state: skippedWorkout(w) ? "skipped" : "done",
        records: (w.records ?? []).length,
        skipped: 0,
      });
    });
  let first = 0;
  for (const t of a.track.filter((x) => x.date === date)) {
    const item: CellItem = { kind: "track", key: "t" + t.key, to: href(model.monday, "track", t.key), track: t.key, label: t.label, logged: t.logged, state: t.state, skipped: t.skipped };
    if (t.before) items.splice(first++, 0, item);
    else items.push(item);
  }
  return items;
}

function isSelected(item: CellItem, sel: WeekSelection): boolean {
  return item.kind === "weights" ? !!item.session && item.session === sel.session : item.track === sel.track;
}

const STATE_MARK: Record<State, string> = { done: "✓", skipped: "⊖", missed: "–", today: "●", planned: "○" };

/** One item on one line: the rest of the plan as its chip (✓ inside when logged), a weights session or a run as a pill in its state. */
function Item({ item }: { item: CellItem }) {
  if (item.kind === "track" && item.state) {
    return (
      <span className={"wk-pill is-" + item.state}>
        <span aria-hidden="true">{STATE_MARK[item.state]}</span>
        <span>{item.label}</span>
        <span className="sr">, {STATE_WORD[item.state]}</span>
        {skippedText(item.state, item.skipped) ? <span className="wk-skipped"> · {skippedText(item.state, item.skipped)}</span> : null}
      </span>
    );
  }
  if (item.kind === "track") {
    return (
      <span className={"wk-plain" + (item.logged ? " is-logged" : "")}>
        {item.label}
        {item.logged ? (
          <span className="wk-logged" aria-hidden="true">
            {" "}✓
          </span>
        ) : null}
        {item.logged ? <span className="sr">, logged</span> : null}
        {!item.logged && item.skipped ? (
          <span className="wk-skip-mark" aria-hidden="true">
            {" "}⊖
          </span>
        ) : null}
        {!item.logged && item.skipped ? <span className="sr">, skipped</span> : null}
      </span>
    );
  }
  return (
    <span className={"wk-pill is-" + item.state}>
      <span aria-hidden="true">{STATE_MARK[item.state]}</span>
      <span>{item.title}</span>
      <span className="sr">, {STATE_WORD[item.state]}</span>
      {skippedText(item.state, item.skipped) ? <span className="wk-skipped"> · {skippedText(item.state, item.skipped)}</span> : null}
      {item.records ? <PbMark n={item.records} /> : null}
    </span>
  );
}

function itemLabel(item: CellItem): string {
  const skipped = skippedText(item.state, item.skipped);
  if (item.kind === "track") return item.label + (item.state ? ": " + STATE_WORD[item.state] + (skipped ? ", " + skipped : "") : item.logged ? ", logged" : item.skipped ? ", skipped" : "");
  return item.title + ": " + STATE_WORD[item.state] + (skipped ? ", " + skipped : "");
}

function CellLink({ item, sel }: { item: CellItem; sel: WeekSelection }) {
  const body = <Item item={item} />;
  const selected = isSelected(item, sel);
  const cl = "wk-item" + (item.kind === "track" ? " is-track" : "") + (selected ? " is-selected" : "");
  return item.to ? (
    <Link className={cl} to={item.to} title={itemLabel(item)} aria-current={selected ? "true" : undefined}>
      {body}
    </Link>
  ) : (
    <span className={cl} title={itemLabel(item)}>
      {body}
    </span>
  );
}

/** How the day's workouts felt (§27.2), short enough for a cell: the effort and Sore or Hurt; the panels say where. */
function Felt({ a, date }: { a: Athlete; date: string }) {
  const felt = a.workouts.filter((w) => w.date === date && (effortOf(w.session) != null || bodyOf(w.session)));
  if (!felt.length) return null;
  return (
    <span className="wk-felt">
      {felt.map((w, i) => {
        const effort = effortOf(w.session);
        const body = bodyOf(w.session);
        return (
          <span key={i}>
            {i ? <span aria-hidden="true"> · </span> : null}
            {effort != null ? (
              <span className="num" title={effort + " · " + effortWord(effort)}>
                <span aria-hidden="true">RPE {effort}</span>
                <span className="sr">{"How hard: " + effort + ", " + effortWord(effort)}</span>
              </span>
            ) : null}
            {effort != null && body ? <span aria-hidden="true"> · </span> : null}
            {body ? <BodyLevel level={body.level} /> : null}
          </span>
        );
      })}
    </span>
  );
}

/** A meet they are going to, in their day (§27.3.1), so a day's column reads who is racing. */
function RaceItem({ meet }: { meet: Meet }) {
  return (
    <Link className="wk-item is-race" to={"/meet/" + encodeURIComponent(meet.id)} aria-label={"Meet: " + meet.name} title={meet.name + (meet.place ? ", " + meet.place : "")}>
      <span className="wk-race">
        <FlagIcon />
        <span>{meet.name}</span>
      </span>
    </Link>
  );
}

const racing = (meets: Meet[], a: Athlete, date: string) => meetsOn(meets, date).filter((m) => m.people.includes(a.id));

function DayCell({ model, a, date, sel, meets }: { model: WeekModel; a: Athlete; date: string; sel: WeekSelection; meets: Meet[] }) {
  const items = cellItems(model, a, date);
  // What is asked of them, a line each; the rest of the plan, which nobody logs, together on one quiet line.
  const owed = items.filter((item) => item.kind === "weights" || item.state);
  const rest = items.filter((item) => !(item.kind === "weights" || item.state));
  const races = racing(meets, a, date);
  const cls = ["wk-cell", date === model.today ? "wk-today" : "", date === sel.date ? "wk-col-selected" : ""].filter(Boolean).join(" ");
  const open = (e: MouseEvent<HTMLTableCellElement>) => {
    if ((e.target as Element).closest("a")) return;
    e.currentTarget.querySelector<HTMLAnchorElement>("a")?.click();
  };
  if (!items.length && !races.length) {
    return (
      <td className={cls}>
        <span className="k-muted" aria-hidden="true">
          —
        </span>
        <span className="sr">Nothing</span>
      </td>
    );
  }
  return (
    <td className={cls} onClick={open}>
      <div className="wk-items">
        {races.map((m) => (
          <RaceItem key={"m" + m.id} meet={m} />
        ))}
        {owed.map((item) => (
          <CellLink key={item.key} item={item} sel={sel} />
        ))}
        {rest.length ? (
          <span className="wk-rest">
            {rest.map((item, i) => (
              <span key={item.key}>
                {i ? <span aria-hidden="true"> · </span> : null}
                <CellLink item={item} sel={sel} />
              </span>
            ))}
          </span>
        ) : null}
        <Felt a={a} date={date} />
      </div>
    </td>
  );
}

interface Row {
  a: Athlete;
  load: AthleteLoad | null;
}

const doneRatio = (r: Row) => (r.a.total ? r.a.done / r.a.total + r.a.done / 1000 : -1);

const SORTS: Record<string, (a: Row, b: Row) => number> = {
  name: byText((r: Row) => r.a.name),
  done: byNumber(doneRatio),
  load: byNumber((r: Row) => loadValue(r.load)),
  prs: byNumber((r: Row) => r.a.prs),
};

function SortHeader({ id, label, sort, setSort, className }: { id: string; label: string; sort: Sort; setSort: (s: Sort) => void; className?: string }) {
  const active = sort.key === id;
  return (
    <th scope="col" className={className} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        className="k-sort"
        onClick={() => setSort(active ? { key: id, dir: sort.dir === "asc" ? "desc" : "asc" } : { key: id, dir: id === "name" ? "asc" : "desc" })}
      >
        <span>{label}</span>
        {active ? (
          <span className="k-sort-mark" aria-hidden="true">
            {sort.dir === "asc" ? "▲" : "▼"}
          </span>
        ) : null}
      </button>
    </th>
  );
}

/** ←/→/↑/↓ move between cells' first links, skipping empty days, so the grid is not a long Tab. */
function moveFocus(e: KeyboardEvent<HTMLTableElement>) {
  if (e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return;
  const step = { ArrowLeft: [0, -1], ArrowRight: [0, 1], ArrowUp: [-1, 0], ArrowDown: [1, 0] }[e.key];
  if (!step) return;
  const cell = (e.target as Element).closest?.("tbody td, tbody th") as HTMLTableCellElement | null;
  if (!cell) return;
  const rows = Array.from(e.currentTarget.tBodies[0]?.rows ?? []);
  let r = rows.indexOf(cell.parentElement as HTMLTableRowElement);
  let c = cell.cellIndex;
  for (;;) {
    r += step[0]!;
    c += step[1]!;
    const next = rows[r]?.cells[c];
    if (!next) return;
    const link = next.querySelector<HTMLAnchorElement>("a");
    if (link) {
      e.preventDefault();
      link.focus();
      return;
    }
  }
}

/** The meets on a day (DESIGN §27.3): the flag and the name, each opening the meet. */
function DayMeets({ meets, day }: { meets: Meet[]; day: string }) {
  const on = meetsOn(meets, day);
  if (!on.length) return null;
  return (
    <span className="wk-meets">
      {on.map((m) => (
        <Link key={m.id} className="wk-meet" to={"/meet/" + encodeURIComponent(m.id)} aria-label={"Meet: " + m.name} title={m.name + (m.place ? ", " + m.place : "")}>
          <FlagIcon />
          <span>{m.name}</span>
        </Link>
      ))}
    </span>
  );
}

export function WeekMatrix({ sel }: { sel: WeekSelection }) {
  const model = useModel();
  const meetsRead = useMeets(model.monday, model.sunday);
  const meets = useMemo(() => meetsRead ?? [], [meetsRead]);
  const loadData = useLoad();
  const notes = useNotes();
  const [sort, setSort] = useState<Sort>({ key: "name", dir: "asc" });
  const trainingDays = useMemo(() => new Set(weekDays(model).map((d) => d.date)), [model]);
  const idle = useMemo(
    () => model.athletes.filter((a) => !a.given.length && !a.workouts.length && !a.track.length && !meets.some((m) => m.people.includes(a.id))),
    [model, meets],
  );
  const rows = useMemo(() => {
    const out = model.athletes.filter((a) => !idle.includes(a)).map((a) => ({ a, load: athleteLoad(loadData, a.id, model.today, model.monday) }));
    out.sort(SORTS[sort.key] ?? SORTS.name!);
    if (sort.dir === "desc") out.reverse();
    return out;
  }, [model, loadData, sort, idle]);
  const selectedTrackDay = sel.track ? model.track.find((t) => t.key === sel.track)?.date : undefined;
  const selectedSessionDay = sel.session ? model.coach.find((c) => c.id === sel.session)?.day : undefined;

  return (
    <>
    <WeekByDay rows={rows} idle={idle} sel={sel} notes={notes} meets={meets} />
    <div className="k-table-wrap wk-matrix-wrap">
      <table className="k-table wk-matrix" aria-label="The week, athletes by day" onKeyDown={moveFocus}>
        <thead>
          <tr>
            <SortHeader id="name" label="Athlete" sort={sort} setSort={setSort} className="k-sticky wk-name" />
            {model.days.map((d) => {
              const today = d === model.today;
              const marked = d === sel.date || d === selectedTrackDay || d === selectedSessionDay;
              const cls = ["wk-day", today ? "wk-today" : "", d === sel.date ? "wk-col-selected" : "", marked ? "is-marked" : ""].filter(Boolean).join(" ");
              const text = (
                <>
                  <span aria-hidden="true">
                    <span className="wk-weekday">{weekdayShort(d)}</span> <span className="num">{parseDay(d).getDate()}</span>
                  </span>
                  <span className="sr">{dayLong(d)}</span>
                  {today ? <span className="wk-today-tag">Today</span> : null}
                </>
              );
              return (
                <th key={d} scope="col" className={cls} title={dayLong(d)}>
                  {trainingDays.has(d) || meetsOn(meets, d).length ? (
                    <Link className="wk-day-link" to={href(model.monday, "day", d)} aria-current={d === sel.date ? "true" : undefined}>
                      {text}
                    </Link>
                  ) : (
                    text
                  )}
                  <DayMeets meets={meets} day={d} />
                </th>
              );
            })}
            <SortHeader id="done" label="Done" sort={sort} setSort={setSort} className="k-end" />
            <SortHeader id="load" label="Load" sort={sort} setSort={setSort} />
            <SortHeader id="prs" label="PBs" sort={sort} setSort={setSort} className="k-end" />
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map(({ a, load }) => <AthleteRow key={a.id} model={model} a={a} load={load} sel={sel} notes={notes} meets={meets} />)
          ) : idle.length ? null : (
            <tr>
              <td className="k-empty" colSpan={model.days.length + 4}>
                No athletes on the team yet. They appear here once they join with their code.
              </td>
            </tr>
          )}
          {idle.length ? (
            <tr className="wk-idle">
              <td colSpan={model.days.length + 4}>
                <span className="k-muted">{rows.length ? "Not in this week's plan: " : "Nobody is in this week's plan yet: "}</span>
                {idle.map((a, i) => (
                  <span key={a.id}>
                    {i ? ", " : ""}
                    <Link to={href(model.monday, "athlete", a.id)}>{a.name}</Link>
                  </span>
                ))}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
    </>
  );
}

/** The week on a phone: a day at a time, a row per athlete with that day's sessions, no sideways scroll. */
function WeekByDay({ rows, idle, sel, notes, meets }: { rows: Row[]; idle: Athlete[]; sel: WeekSelection; notes: Note[] | undefined; meets: Meet[] }) {
  const model = useModel();
  const first = model.days.includes(model.today) ? model.today : (weekDays(model)[0]?.date ?? model.days[0]!);
  const [day, setDay] = useState(sel.date ?? first);
  return (
    <div className="wk-phone">
      <div className="wk-phone-days" role="radiogroup" aria-label="Day">
        {model.days.map((d) => (
          <button key={d} type="button" role="radio" aria-checked={d === day} className={"wk-phone-day" + (d === day ? " is-on" : "") + (d === model.today ? " is-today" : "")} onClick={() => setDay(d)}>
            <span>{weekdayShort(d)}</span>
            <b className="num">{parseDay(d).getDate()}</b>
          </button>
        ))}
      </div>
      <DayMeets meets={meets} day={day} />
      <ul className="wk-phone-list" aria-label={dayLong(day)}>
        {rows.map(({ a, load }) => {
          const items = cellItems(model, a, day);
          const races = racing(meets, a, day);
          const column = columnText(a);
          return (
            <li key={a.id} className="wk-phone-row">
              <div className="wk-phone-who">
                <Link className="k-row-link" to={href(model.monday, "athlete", a.id)}>
                  {a.name}
                </Link>
                {hasNotes(notes, a.id) ? <NoteMark /> : null}
                {column ? <span className="k-sub">{column}</span> : null}
              </div>
              <PinnedLine note={pinnedFor(notes, a.id)} />
              <div className="wk-phone-items">
                {races.map((m) => (
                  <RaceItem key={"m" + m.id} meet={m} />
                ))}
                {items.length ? (
                  items.map((item) =>
                    item.to ? (
                      <Link key={item.key} className="wk-item" to={item.to} title={itemLabel(item)}>
                        <Item item={item} />
                      </Link>
                    ) : (
                      <span key={item.key} className="wk-item" title={itemLabel(item)}>
                        <Item item={item} />
                      </span>
                    ),
                  )
                ) : races.length ? null : (
                  <span className="k-muted">Nothing</span>
                )}
              </div>
              <div className="wk-phone-week k-muted">
                {a.total ? (
                  <span className="num">
                    {a.done} of {a.total} this week{a.skipped ? ` · ${a.skipped} skipped` : ""}
                  </span>
                ) : null}
                {load && (load.status.trend === "high" || load.status.trend === "low") ? <Status kind={load.status.trend} text={load.status.trend === "high" ? "High load" : "Low load"} /> : null}
              </div>
            </li>
          );
        })}
      </ul>
      {idle.length ? (
        <p className="wk-phone-idle k-muted">
          Not in this week's plan:{" "}
          {idle.map((a, i) => (
            <span key={a.id}>
              {i ? ", " : ""}
              <Link to={href(model.monday, "athlete", a.id)}>{a.name}</Link>
            </span>
          ))}
        </p>
      ) : null}
    </div>
  );
}

function AthleteRow({ model, a, load, sel, notes, meets }: { model: WeekModel; a: Athlete; load: AthleteLoad | null; sel: WeekSelection; notes: Note[] | undefined; meets: Meet[] }) {
  const column = columnText(a);
  let done: ReactNode;
  if (a.total) {
    done = (
      <span className="num">
        {a.done} of {a.total}
        {a.skipped ? <span className="k-muted"> · {a.skipped} skipped</span> : null}
      </span>
    );
  } else done = <span className="k-muted">{a.workouts.length ? plural(a.workouts.length, "workout", "workouts") : "—"}</span>;
  return (
    <tr>
      <th scope="row" className="k-sticky wk-name">
        <Link className="k-row-link" to={href(model.monday, "athlete", a.id)}>
          {a.name}
        </Link>
        {hasNotes(notes, a.id) ? <NoteMark /> : null}
        {column ? <span className="k-sub">{column}</span> : null}
        <PinnedLine note={pinnedFor(notes, a.id)} />
      </th>
      {model.days.map((d) => (
        <DayCell key={d} model={model} a={a} date={d} sel={sel} meets={meets} />
      ))}
      <td className="k-end">{done}</td>
      <td>
        <LoadCell load={load} />
      </td>
      <td className="k-end">{a.prs ? <PbMark n={a.prs} /> : <span className="k-muted">—</span>}</td>
    </tr>
  );
}

